import type { ReactElement } from 'react';
import { Box, Card, CardContent, Stack, Typography } from '@mui/material';
import AirplanemodeActiveOutlined from '@mui/icons-material/AirplanemodeActiveOutlined';
import DirectionsBoatOutlined from '@mui/icons-material/DirectionsBoatOutlined';
import PercentOutlined from '@mui/icons-material/PercentOutlined';
import TrendingUpOutlined from '@mui/icons-material/TrendingUpOutlined';
import ViewInArOutlined from '@mui/icons-material/ViewInArOutlined';
import type { KpiId } from '@serb/shared';
import { KpiTarget } from '../../components/KpiTarget';
import { StatTile } from '../../components/StatTile';
import { TileGrid } from '../../components/TileGrid';
import { toneColor, type Tone } from '../../components/tone';
import { jod, jodPrecise, percent } from '../../lib/currency';
import type { summarize } from './calc';

export type FinanceTile = { title: string; note: string; value: string; icon: ReactElement; tone: Tone };

/** A row of money tiles: a small number like "JOD 148,250", a title, and a note */
export const FinanceTiles = ({ tiles, lg = 3, xl = 6 }: { tiles: FinanceTile[]; lg?: number; xl?: number }) => (
  <TileGrid lg={lg} xl={xl}>
    {tiles.map((t) => (
      <StatTile key={t.title} count={t.value} title={t.title} note={t.note} icon={t.icon} tone={t.tone} compact />
    ))}
  </TileGrid>
);

type Summary = ReturnType<typeof summarize>;
/** A big centred number with an icon above and a label and note below: the same look as each block in the unit card */
function HeadlineCard({
  icon,
  tone,
  label,
  value,
  note,
  kpi,
  actual,
}: {
  icon: ReactElement;
  tone: Tone;
  label: string;
  value: string;
  note: string;
  kpi?: KpiId;
  actual?: number;
}) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ height: '100%', display: 'flex' }}>
        <Stack
          spacing={0.25}
          sx={{
            flex: 1,
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            p: { xs: 0.5, sm: 1.5 },
            borderRadius: 2,
            bgcolor: 'action.hover',
          }}
        >
          <Box sx={{ color: toneColor(tone), display: 'flex' }}>{icon}</Box>
          <Typography sx={{ fontWeight: 700, fontSize: 13 }}>{label}</Typography>
          <Typography sx={{ fontWeight: 800, fontSize: { xs: 22, sm: 30 }, lineHeight: 1.15 }}>{value}</Typography>
          <Typography variant="caption" color="text.secondary">
            {note}
          </Typography>
          {kpi && <KpiTarget id={kpi} actual={actual ?? null} />}
        </Stack>
      </CardContent>
    </Card>
  );
}

type Unit = {
  kpi: KpiId;
  actual: number | null;
  label: string;
  per: string;
  icon: ReactElement;
  value: string;
  shipments: number;
};

/** FCL, air and LCL side by side with the same big number, so the three are easy to compare */
function UnitProfitCard({ s }: { s: Summary }) {
  const units: Unit[] = [
    {
      kpi: 'profitPerContainer',
      actual: s.perContainer?.value ?? null,
      label: 'FCL',
      per: 'per container',
      icon: <DirectionsBoatOutlined />,
      value: s.perContainer ? jod(s.perContainer.value) : '–',
      shipments: s.perContainer?.shipments ?? 0,
    },
    {
      kpi: 'profitPerKg',
      actual: s.perKg?.value ?? null,
      label: 'Air Freight',
      per: 'per KG',
      icon: <AirplanemodeActiveOutlined />,
      value: s.perKg ? jodPrecise(s.perKg.value) : '–',
      shipments: s.perKg?.shipments ?? 0,
    },
    {
      kpi: 'profitPerCbm',
      actual: s.perCbm?.value ?? null,
      label: 'LCL',
      per: 'per CBM',
      icon: <ViewInArOutlined />,
      value: s.perCbm ? jodPrecise(s.perCbm.value) : '–',
      shipments: s.perCbm?.shipments ?? 0,
    },
  ];
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 1.5 }}>
          Average Profit per Unit
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: { xs: 1, sm: 2 } }}>
          {units.map((u) => (
            <Stack
              key={u.label}
              spacing={0.25}
              sx={{
                alignItems: 'center',
                textAlign: 'center',
                p: { xs: 1, sm: 1.5 },
                borderRadius: 2,
                bgcolor: 'action.hover',
              }}
            >
              <Box sx={{ color: toneColor('info'), display: 'flex' }}>{u.icon}</Box>
              <Typography sx={{ fontWeight: 700, fontSize: 13 }}>{u.label}</Typography>
              <Typography
                sx={{ fontWeight: 800, fontSize: { xs: 15, sm: 26 }, whiteSpace: 'nowrap', lineHeight: 1.15 }}
              >
                {u.value}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {u.per}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {u.shipments ? `${u.shipments} shipment${u.shipments === 1 ? '' : 's'}` : 'no shipments yet'}
              </Typography>
              <KpiTarget id={u.kpi} actual={u.actual} />
            </Stack>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
}

/** The headline numbers: profit and the average margin, then the average profit per unit (FCL, air, LCL) side by side; extra tiles after */
export function ProfitTiles({
  s,
  extra = [],
  lg = 4,
  xl = 7,
}: {
  s: Summary;
  extra?: FinanceTile[];
  lg?: number;
  xl?: number;
}) {
  return (
    <Stack spacing={{ xs: 1.25, md: 2 }}>
      <Box
        sx={{
          display: 'grid',
          gap: { xs: 1.25, md: 2 },
          gridTemplateColumns: {
            xs: 'repeat(2, minmax(0, 1fr))',
            lg: 'minmax(0, 1fr) minmax(0, 1fr) minmax(0, 2.6fr)',
          },
          alignItems: 'stretch',
        }}
      >
        <HeadlineCard
          icon={<TrendingUpOutlined />}
          tone="success"
          label="Gross Profit"
          value={jod(s.profit)}
          note="Revenue minus costs"
        />
        <HeadlineCard
          icon={<PercentOutlined />}
          tone="info"
          label="Avg. Profit Margin"
          value={percent(s.avgMargin)}
          note="Average of each shipment's margin"
          kpi="avgMargin"
          actual={s.avgMargin}
        />
        <Box sx={{ gridColumn: { xs: '1 / -1', lg: 'auto' } }}>
          <UnitProfitCard s={s} />
        </Box>
      </Box>
      {extra.length > 0 && <FinanceTiles tiles={extra} lg={lg} xl={xl} />}
    </Stack>
  );
}
